import CommentLayout from "app/uicomponents/macrocomponents/Comments/CommentLayout";
import { getSessionStore } from "app/utils/sessionStore";
import { ProposalTypes } from "app/utils/types";
import { getThemeColors } from "app/utils/utils";
import { Card } from "primereact/card";
import PropTypes from "prop-types";
import { Fragment, useState } from "react";
import { FaCaretRight } from "react-icons/fa";
import { useIPContext } from "../../InteractiveProposalProvider";

interface InteractiveProposalSolutionInsightsProps {
  isDarkTheme: boolean;
}

const proposalContent = {
  [ProposalTypes.BuySell]: {
    sections: [
      {
        title: "Triggering Events",
        content:
          "Triggering events consist of departures (termination or retirement), divorce, disability, and death. When an event is triggered, the prevailing buy-sell agreement must be followed. For example, when a partner dies the buy-sell agreement may likely force the newly deceased partner to sell their respective share according to the valuation method and other provisions set forth in the buy-sell agreement.",
      },
      {
        title: "Valuation Method",
        content:
          "The valuation method sets forth how the company will be valued. Common methods may include using a formula such as multiples of EBITDA, multiples of revenue, or agreed fair market value at the time of sale.",
      },
      {
        title: "Funding Strategy",
        content:
          "Insurance is a popular funding strategy because it can be neatly designed to meet two triggering events (death and disability), at a very reasonable price. Financing the risk with insurance allows cash and assets to be deployed to higher and better uses for the business, for example on capital investments, R&D, or other investments to grow the business.",
      },
    ],
  },
  [ProposalTypes.SplitDollar]: {
    sections: [
      {
        title: "Plan Agreement",
        content:
          "A company enters into a written split dollar agreement with a key employee, which results in the purchase of a cash-value life insurance policy.",
      },
      {
        title: "Plan Funding",
        content:
          "Financing from the company to the key employee provides the money needed to pay the premium on the policy. A portion of the cash value and death benefit equal to the financed amount is assigned to the company as collateral.",
      },
      {
        title: "Plan Termination",
        content:
          "At plan termination, the financed amount is paid off or forgiven and the collateral assignment is removed. This gives the employee sole access to the remaining policy benefits for supplemental retirement income and/or survivor benefits.",
      },
    ],
  },
  [ProposalTypes.KeyPersonInsurance]: {
    sections: [
      {
        title: "Identify and Insure",
        content:
          "The business selects a key employee whose loss would significantly impact operations and takes out a life or disability insurance policy on their behalf.",
      },
      {
        title: "Pay Premiums",
        content:
          "The business pays regular premiums for the policy, maintaining coverage and ensuring the financial protection is in place.",
      },
      {
        title: "Claim and Benefit",
        content:
          "In the event of the key person's death or disability, the business files a claim and receives a payout, which can be used to manage the financial impact, cover expenses, and stabilize the company during the transition.",
      },
    ],
  },
  [ProposalTypes.PremiumFinance]: {
    sections: [
      {
        title: "Apply & Qualify ",
        content:
          "The client applies for both life insurance coverage and premium financing. The insurance carrier evaluates the insured for coverage, while the lender evaluates the borrower’s financial strength and ability to support the financing. Once approved, the insurance policy and financing arrangement can be established.",
      },
      {
        title: "Finance the Premiums",
        content:
          "The lender finances all or a portion of the life insurance premiums according to the financing agreement. The client is generally responsible for interest and any required collateral. Collateral requirements may change over time based on policy performance, interest rates, and the outstanding financing balance.",
      },
      {
        title: "Exit the Financing",
        content:
          "At a future point, the financing is repaid according to the planned exit strategy. Repayment may come from policy values, outside assets, or a combination of both. If death occurs while financing remains outstanding, the applicable financing obligation is generally satisfied from the policy proceeds.",
      },
    ],
  },
};

const InteractiveProposalSolutionInsights: React.FC<InteractiveProposalSolutionInsightsProps> = ({
  isDarkTheme,
}) => {
  const [expanded, setExpanded] = useState<boolean[]>([true, false, false]);
  const { branding } = getSessionStore("lookUpResponse");
  const { primaryColor } = getThemeColors(branding);
  const { summaryData: proposalSummary } = useIPContext();
  // In the Interactive Review Center the context summary may not carry
  // `proposalType` (it's a plan view), so fall back to the plan type from
  // session — same fallback the IRC header uses. Without this, contacts viewing
  // a plan get an empty `proposalContent[...]` lookup and no step cards render.
  const proposalType = (proposalSummary?.proposalType ??
    getSessionStore("planInfo")?.planType) as ProposalTypes;

  const toggleSection = (index: number) => {
    setExpanded((prev) => prev.map((exp, i) => (i === index ? !exp : exp)));
  };

  const sections = proposalContent[proposalType]?.sections || [];

  return (
    <CommentLayout
      commentLayoutId="interactive-proposal-solution-insights"
      className={`min-h-screen flex flex-col items-center px-2 ${isDarkTheme ? "dark-theme" : ""}`}
    >
      <div className="w-full max-w-6xl">
        <h3
          className={`text-lg font-semibold conthrax-font ${
            isDarkTheme ? "text-white" : "text-gray-700"
          }`}
        >
          Overview
        </h3>
        <p className={`${isDarkTheme ? "text-[#A9A9BC]" : "text-gray-600"} mt-2`}>
          {proposalType === ProposalTypes.BuySell
            ? "A funded buy-sell agreement is critical to any multi-owner business. The buy-sell agreement can ensure company ownership continues in a manner that is in the best interests of the company and fair to the owners by spelling out what happens under different triggering events."
            : proposalType === ProposalTypes.SplitDollar
            ? "A Split Dollar (Loan Regime) plan can reward select key employees by helping them enhance their retirement income and provide survivor benefits for their family"
            : proposalType === ProposalTypes.PremiumFinance
            ? "Premium financing is a strategy that allows affluent individuals, families, and business owners to fund life insurance premiums using third-party financing. Rather than using significant amounts of personal capital to pay premiums, the client may preserve liquidity and keep assets available for other investment, business, or planning opportunities."
            : "Key Person provides financial protection for a business by covering the loss of an essential employee, ensuring stability and continuity in the face of unexpected disruptions."}
        </p>
        <h4 className={`mt-4 font-semibold ${isDarkTheme ? "text-[#A9A9BC]" : "text-gray-700"}`}>
          {proposalType === ProposalTypes.BuySell
            ? "Here's how it works: A buy-sell agreement consists of three common elements:"
            : "Here's how it works:"}
        </h4>
      </div>

      <div className="w-full max-w-6xl mt-3">
        <div className="grid grid-cols-1 md:grid-cols-[1fr,auto,1fr,auto,1fr] items-start gap-2">
          {sections.map((section, index) => (
            <Fragment key={section.title}>
              <Card
                key={section.title}
                className={`shadow-md rounded-xl p-0 ${
                  isDarkTheme ? "bg-[#3A3A50]" : "bg-[#f7f7f8]"
                } ${expanded[index] ? "h-auto" : "h-[70px]"}`}
                pt={{
                  content: { className: "!pt-0" },
                }}
              >
                <div
                  className="flex items-center cursor-pointer"
                  onClick={() => toggleSection(index)}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="!text-xs rounded-md text-white font-semibold px-2 py-1 conthrax-font"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {index + 1}
                    </div>
                    <p
                      className={`font-semibold !text-xs conthrax-font ${
                        isDarkTheme ? "text-[#f7f7f8]" : "text-gray-700"
                      }`}
                    >
                      {section.title}
                    </p>
                  </div>
                </div>
                {expanded[index] && (
                  <div
                    className={`mt-3 p-4 rounded-md shadow ${
                      isDarkTheme ? "bg-[#1E1E29]" : "bg-white"
                    }`}
                  >
                    <p
                      className={`text-sm leading-relaxed whitespace-pre-line ${
                        isDarkTheme ? "text-[#f7f7f8]" : "text-gray-600"
                      }`}
                    >
                      {section.content}
                    </p>
                  </div>
                )}
              </Card>
              {index < sections.length - 1 && (
                <div className="pt-[20px]">
                  <FaCaretRight style={{ fontSize: "30px", color: "#8A8AA3" }} />
                </div>
              )}
            </Fragment>
          ))}
        </div>
      </div>
    </CommentLayout>
  );
};

InteractiveProposalSolutionInsights.propTypes = {
  isDarkTheme: PropTypes.bool.isRequired,
};

export default InteractiveProposalSolutionInsights;
